import React, { useState, useMemo } from 'react';
import { Plus, TrendingUp, TrendingDown, DollarSign, Filter, Download, Users, ChevronLeft, ChevronRight, CalendarDays, RefreshCw, Zap } from 'lucide-react';
import AsaasPaymentModal from '@/components/AsaasPaymentModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate } from '@/utils/helpers';
import api from '@/utils/api';
import useApiData from '@/hooks/useApiData';
import { toast } from 'sonner';


const CATEGORIAS_DESPESAS = [
  'Freelancer',
  'Ferramenta/Software',
  'Marketing',
  'Infraestrutura',
  'Equipamento',
  'Aluguel/Espaço',
  'Salários',
  'Impostos',
  'Outros'
];

export default function FinanceiroUnificado() {
  const { data: paymentsData, refresh: refreshPayments } = useApiData('/payments');
  const { data: expensesData, refresh: refreshExpenses } = useApiData('/expenses');
  const { data: clientsData } = useApiData('/clients');
  const { data: funcionariosData } = useApiData('/advanced/funcionarios');
  const { data: custosFixosData } = useApiData('/advanced/custos-fixos');

  const payments = paymentsData || [];
  const expenses = expensesData || [];
  const clients = clientsData || [];
  const funcionarios = funcionariosData || [];
  const custosFixos = Array.isArray(custosFixosData) ? custosFixosData : [];

  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [asaasPayment, setAsaasPayment] = useState(null); // pagamento selecionado para modal Asaas

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return { month: now.getMonth(), year: now.getFullYear() };
  });

  const goToPrevMonth = () => {
    setSelectedMonth(prev => {
      if (prev.month === 0) return { month: 11, year: prev.year - 1 };
      return { month: prev.month - 1, year: prev.year };
    });
  };

  const goToNextMonth = () => {
    setSelectedMonth(prev => {
      if (prev.month === 11) return { month: 0, year: prev.year + 1 };
      return { month: prev.month + 1, year: prev.year };
    });
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setSelectedMonth({ month: now.getMonth(), year: now.getFullYear() });
  };

  const monthLabel = new Date(selectedMonth.year, selectedMonth.month).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const isInSelectedMonth = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d.getMonth() === selectedMonth.month && d.getFullYear() === selectedMonth.year;
  };

  const [expenseFormData, setExpenseFormData] = useState({
    value: '',
    date: new Date().toISOString().split('T')[0],
    category: '',
    description: ''
  });

  const [paymentFormData, setPaymentFormData] = useState({
    client_id: '',
    value: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });

  const [loading, setLoading] = useState(false);

  // Stats calculados com useMemo (sem useEffect + setState extra)
  const stats = useMemo(() => {
    const result = { previsto: 0, pago: 0, em_aberto: 0, atrasado: 0, total_despesas: 0, custo_equipe: 0, custos_fixos_total: 0 };
    const monthPayments = payments.filter(p => isInSelectedMonth(p.due_date));
    monthPayments.forEach(payment => {
      if (payment.status === 'pago') result.pago += payment.value;
      else if (payment.status === 'previsto') result.previsto += payment.value;
      else if (payment.status === 'em_aberto') result.em_aberto += payment.value;
      else if (payment.status === 'atrasado') result.atrasado += payment.value;
    });
    const custoEquipe = funcionarios.filter(f => f.is_active).reduce((sum, f) => sum + f.custo_mensal, 0);
    result.custo_equipe = custoEquipe;
    const custosFixosTotal = custosFixos.reduce((sum, c) => sum + (c.valor_mensal || c.valor || 0), 0);
    result.custos_fixos_total = custosFixosTotal;
    const monthExpenses = expenses.filter(e => isInSelectedMonth(e.date));
    result.total_despesas = monthExpenses.reduce((sum, exp) => sum + exp.value, 0) + custoEquipe + custosFixosTotal;
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, expenses, funcionarios, custosFixos, selectedMonth]);

  const handleMarkAsPaid = async (paymentId) => {
    if (!window.confirm('Deseja realmente marcar esta cobrança como paga?')) return;
    try {
      await api.put(`/payments/${paymentId}/pay`);
      toast.success('Pagamento marcado como pago!');
      refreshPayments();

    } catch (error) {
      toast.error('Erro ao atualizar pagamento');
    }
  };

  const handleCancelPayment = async (paymentId) => {
    if (!window.confirm('Deseja realmente cancelar esta cobrança?')) return;
    try {
      await api.put(`/payments/${paymentId}`, { status: 'cancelado' });
      toast.success('Cobrança cancelada!');
      refreshPayments();
    } catch (error) {
      toast.error('Erro ao cancelar cobrança');
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...expenseFormData,
        value: parseFloat(expenseFormData.value),
        date: new Date(expenseFormData.date).toISOString()
      };
      await api.post('/expenses', payload);
      toast.success('Despesa criada com sucesso!');
      setIsExpenseDialogOpen(false);
      resetExpenseForm();
      refreshExpenses();

    } catch (error) {
      toast.error('Erro ao criar despesa');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        client_id: paymentFormData.client_id,
        value: parseFloat(paymentFormData.value),
        due_date: new Date(paymentFormData.date).toISOString(),
        description: paymentFormData.description
      };
      await api.post('/payments', payload);
      toast.success('Cobrança avulsa criada com sucesso!');
      setIsPaymentDialogOpen(false);
      resetPaymentForm();
      refreshPayments();

    } catch (error) {
      toast.error('Erro ao criar cobrança avulsa');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Deseja realmente excluir esta despesa?')) return;

    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success('Despesa excluída!');
      refreshExpenses();

    } catch (error) {
      toast.error('Erro ao excluir despesa');
    }
  };

  const resetExpenseForm = () => {
    setExpenseFormData({
      value: '',
      date: new Date().toISOString().split('T')[0],
      category: '',
      description: ''
    });
  };

  const resetPaymentForm = () => {
    setPaymentFormData({
      client_id: '',
      value: '',
      date: new Date().toISOString().split('T')[0],
      description: ''
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      previsto: { variant: 'secondary', label: 'Previsto' },
      em_aberto: { variant: 'default', label: 'Em Aberto' },
      pago: { variant: 'default', label: 'Pago', className: 'bg-success text-white' },
      atrasado: { variant: 'destructive', label: 'Atrasado' },
      cancelado: { variant: 'outline', label: 'Cancelado' }
    };
    const { variant, label, className } = config[status] || config.previsto;
    return <Badge variant={variant} className={className}>{label}</Badge>;
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'N/A';
  };

  const monthPayments = payments.filter(p => isInSelectedMonth(p.due_date));
  const filteredPayments = filterStatus === 'all'
    ? monthPayments
    : monthPayments.filter(p => p.status === filterStatus);

  const monthExpenses = expenses.filter(e => isInSelectedMonth(e.date));
  const filteredExpenses = filterCategory === 'all'
    ? monthExpenses
    : monthExpenses.filter(e => e.category === filterCategory);

  const lucroLiquido = stats.pago - stats.total_despesas;

  const isCurrentMonth = (() => {
    const now = new Date();
    return selectedMonth.month === now.getMonth() && selectedMonth.year === now.getFullYear();
  })();

  return (
    <div className="space-y-6" data-testid="financeiro-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Financeiro</h1>
          <p className="text-slate-600 mt-1">Gestão completa de receitas e despesas</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-[180px] text-center">
            <div className="flex items-center justify-center gap-2">
              <CalendarDays className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold capitalize">{monthLabel}</span>
            </div>
          </div>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isCurrentMonth && (
            <Button variant="ghost" size="sm" onClick={goToCurrentMonth} className="ml-2">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Mês Atual
            </Button>
          )}
        </div>
      </div>

      {/* KPIs Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center text-success mb-3">
            <TrendingUp className="h-5 w-5" />
          </div>
          <p className="text-xs font-medium text-slate-500 uppercase mb-1">Recebido</p>
          <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.pago)}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-10 w-10 rounded-full bg-error/10 flex items-center justify-center text-error mb-3">
            <TrendingDown className="h-5 w-5" />
          </div>
          <p className="text-xs font-medium text-slate-500 uppercase mb-1">Despesas</p>
          <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.total_despesas)}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-3">
            <DollarSign className="h-5 w-5" />
          </div>
          <p className="text-xs font-medium text-slate-500 uppercase mb-1">Lucro Líquido</p>
          <p className={`text-2xl font-bold ${lucroLiquido >= 0 ? 'text-success' : 'text-error'}`}>
            {formatCurrency(lucroLiquido)}
          </p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-10 w-10 rounded-full bg-warning/10 flex items-center justify-center text-warning mb-3">
            <Filter className="h-5 w-5" />
          </div>
          <p className="text-xs font-medium text-slate-500 uppercase mb-1">A Receber</p>
          <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.previsto + stats.em_aberto)}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 mb-3">
            <TrendingDown className="h-5 w-5" />
          </div>
          <p className="text-xs font-medium text-slate-500 uppercase mb-1">Atrasado</p>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.atrasado)}</p>
        </div>
      </div>

      {/* Tabs Recebimentos e Despesas */}
      <Tabs defaultValue="recebimentos" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="recebimentos">Recebimentos</TabsTrigger>
          <TabsTrigger value="despesas">Despesas</TabsTrigger>
          <TabsTrigger value="resumo">Resumo</TabsTrigger>
        </TabsList>

        {/* Tab Recebimentos */}
        <TabsContent value="recebimentos" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Label>Filtrar por status:</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="previsto">Previsto</SelectItem>
                  <SelectItem value="em_aberto">Em Aberto</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="atrasado">Atrasado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Cobrança Avulsa
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Nova Cobrança Avulsa</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreatePayment} className="space-y-4">
                  <div>
                    <Label htmlFor="client_id">Cliente *</Label>
                    <Select
                      value={paymentFormData.client_id}
                      onValueChange={(val) => setPaymentFormData({ ...paymentFormData, client_id: val })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o Cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pay_value">Valor (R$) *</Label>
                      <Input
                        id="pay_value"
                        type="number"
                        step="0.01"
                        value={paymentFormData.value}
                        onChange={(e) => setPaymentFormData({ ...paymentFormData, value: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="pay_date">Vencimento *</Label>
                      <Input
                        id="pay_date"
                        type="date"
                        value={paymentFormData.date}
                        onChange={(e) => setPaymentFormData({ ...paymentFormData, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="pay_desc">Descrição</Label>
                    <Input
                      id="pay_desc"
                      placeholder="Ex: Consultoria extra, Serviço avulso"
                      value={paymentFormData.description}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, description: e.target.value })}
                    />
                  </div>

                  <div className="flex justify-end gap-3 mt-4">
                    <Button type="button" variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={loading}>
                      {loading ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50/50 border-b border-slate-100">
                  <tr>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Valor</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Vencimento</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-slate-500">Nenhum recebimento encontrado</td>
                    </tr>
                  ) : (
                    filteredPayments.map((payment) => (
                      <tr key={payment.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="p-4 text-sm font-medium text-slate-700">{getClientName(payment.client_id)}</td>
                        <td className="p-4 text-sm font-mono text-slate-900">{formatCurrency(payment.value)}</td>
                        <td className="p-4 text-sm text-slate-600">{formatDate(payment.due_date)}</td>
                        <td className="p-4 text-sm">{getStatusBadge(payment.status)}</td>
                        <td className="p-4 text-sm">
                          <div className="flex items-center gap-2">
                            {payment.status !== 'pago' && payment.status !== 'cancelado' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMarkAsPaid(payment.id)}
                              >
                                Marcar como Pago
                              </Button>
                            )}
                            {payment.status !== 'pago' && payment.status !== 'cancelado' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleCancelPayment(payment.id)}
                              >
                                Cancelar
                              </Button>
                            )}
                            {/* Botão Asaas */}
                            {payment.status !== 'pago' && payment.status !== 'cancelado' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`asaas-row-btn ${payment.asaas_payment_id ? 'asaas-row-btn--sent' : ''}`}
                                title={payment.asaas_payment_id ? 'Gerenciar cobrança Asaas' : 'Cobrar via Asaas'}
                                onClick={() => setAsaasPayment(payment)}
                              >
                                <Zap size={14} />
                                {payment.asaas_payment_id ? 'Asaas ✓' : 'Asaas'}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Tab Despesas */}
        <TabsContent value="despesas" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Label>Filtrar por categoria:</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {CATEGORIAS_DESPESAS.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Dialog open={isExpenseDialogOpen} onOpenChange={setIsExpenseDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Despesa
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Nova Despesa</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateExpense} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="value">Valor (R$) *</Label>
                      <Input
                        id="value"
                        type="number"
                        step="0.01"
                        value={expenseFormData.value}
                        onChange={(e) => setExpenseFormData({ ...expenseFormData, value: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="date">Data *</Label>
                      <Input
                        id="date"
                        type="date"
                        value={expenseFormData.date}
                        onChange={(e) => setExpenseFormData({ ...expenseFormData, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Categoria *</Label>
                    <Select
                      value={expenseFormData.category}
                      onValueChange={(value) => setExpenseFormData({ ...expenseFormData, category: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIAS_DESPESAS.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="description">Descrição *</Label>
                    <textarea
                      id="description"
                      className="w-full min-h-[100px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                      value={expenseFormData.description}
                      onChange={(e) => setExpenseFormData({ ...expenseFormData, description: e.target.value })}
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setIsExpenseDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={loading}>
                      {loading ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50/50 border-b border-slate-100">
                  <tr>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Categoria</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Valor</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-slate-500">Nenhuma despesa encontrada</td>
                    </tr>
                  ) : (
                    filteredExpenses.map((expense) => (
                      <tr key={expense.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="p-4 text-sm text-slate-600">{formatDate(expense.date)}</td>
                        <td className="p-4 text-sm">
                          <Badge variant="outline">{expense.category}</Badge>
                        </td>
                        <td className="p-4 text-sm text-slate-600">{expense.description}</td>
                        <td className="p-4 text-sm font-mono text-slate-900">{formatCurrency(expense.value)}</td>
                        <td className="p-4 text-sm">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Excluir
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Custos Fixos Automáticos */}
          {custosFixos.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                <RefreshCw className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-heading font-semibold text-slate-900">Custos Fixos (Recorrentes)</h3>
                <Badge variant="outline" className="ml-auto">
                  Total: {formatCurrency(stats.custos_fixos_total)}
                </Badge>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50/50 border-b border-slate-100">
                    <tr>
                      <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Nome</th>
                      <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Categoria</th>
                      <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {custosFixos.map((custo) => (
                      <tr key={custo.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="p-4 text-sm font-medium text-slate-900">{custo.descricao || custo.nome}</td>
                        <td className="p-4 text-sm">
                          <Badge variant="outline">{custo.categoria}</Badge>
                        </td>
                        <td className="p-4 text-sm font-mono text-error">{formatCurrency(custo.valor_mensal || custo.valor || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Custos de Funcionários */}
          {funcionarios.filter(f => f.is_active).length > 0 && (
            <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-heading font-semibold text-slate-900">Custos de Funcionários (Mensal)</h3>
                <Badge variant="outline" className="ml-auto">
                  Total: {formatCurrency(stats.custo_equipe)}
                </Badge>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50/50 border-b border-slate-100">
                    <tr>
                      <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Nome</th>
                      <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cargo</th>
                      <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                      <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Custo Mensal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funcionarios.filter(f => f.is_active).map((func) => (
                      <tr key={func.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="p-4 text-sm font-medium text-slate-900">{func.nome}</td>
                        <td className="p-4 text-sm text-slate-600">{func.cargo}</td>
                        <td className="p-4 text-sm">
                          <Badge variant={func.tipo === 'fixo' ? 'default' : 'secondary'}>
                            {func.tipo === 'fixo' ? 'CLT' : 'Freelancer'}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm font-mono text-error">{formatCurrency(func.custo_mensal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Tab Resumo */}
        <TabsContent value="resumo" className="space-y-4">
          <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-6">Resumo Financeiro</h3>

            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Total Recebido</span>
                <span className="text-lg font-bold text-success">{formatCurrency(stats.pago)}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Despesas Avulsas</span>
                <span className="text-lg font-bold text-error">{formatCurrency(monthExpenses.reduce((s, e) => s + e.value, 0))}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Custos Fixos</span>
                <span className="text-lg font-bold text-error">{formatCurrency(stats.custos_fixos_total)}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Custo Equipe (Funcionários)</span>
                <span className="text-lg font-bold text-error">{formatCurrency(stats.custo_equipe)}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600 font-medium">Total Despesas</span>
                <span className="text-lg font-bold text-error">{formatCurrency(stats.total_despesas)}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600 font-semibold">Lucro Líquido</span>
                <span className={`text-2xl font-bold ${lucroLiquido >= 0 ? 'text-success' : 'text-error'}`}>
                  {formatCurrency(lucroLiquido)}
                </span>
              </div>

              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-slate-900 mb-3">A Receber</h4>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-600">Previsto</span>
                  <span className="text-sm font-mono">{formatCurrency(stats.previsto)}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-600">Em Aberto</span>
                  <span className="text-sm font-mono">{formatCurrency(stats.em_aberto)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600 font-medium">Atrasado</span>
                  <span className="text-sm font-mono text-red-600">{formatCurrency(stats.atrasado)}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-slate-900 mb-3">Despesas por Categoria</h4>
                <div className="space-y-2">
                  {CATEGORIAS_DESPESAS.map(cat => {
                    const total = expenses
                      .filter(e => e.category === cat)
                      .reduce((sum, e) => sum + e.value, 0);
                    if (total === 0) return null;
                    return (
                      <div key={cat} className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">{cat}</span>
                        <span className="text-sm font-mono">{formatCurrency(total)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal Asaas */}
      {asaasPayment && (
        <AsaasPaymentModal
          payment={asaasPayment}
          clientName={getClientName(asaasPayment.client_id)}
          isOpen={!!asaasPayment}
          onClose={() => setAsaasPayment(null)}
          onSuccess={() => {
            refreshPayments();
            setAsaasPayment(null);
          }}
        />
      )}
    </div>
  );
}